// @ts-nocheck
import {ArrowDown, ArrowUp, Wallet, X} from "@phosphor-icons/react";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {trackDepositButtonClick} from "../../providers/analytics";
import {useAuthStore} from "../../store/useAuth.store";
import {useBalanceStore} from "../../store/useBalance.store";

/* ── Layout container ─────────────────────────────────────── */
const walletContainerCls =
  "hidden max-[799px]:flex fixed bottom-[190px] right-[15px] z-[999] flex-col-reverse items-center gap-2";

/* ── Main FAB trigger ─────────────────────────────────────── */
const walletButtonCls =
  "w-[68px] h-[68px] rounded-full cursor-pointer flex items-center justify-center transition-all duration-300 " +
  "bg-gradient-to-b from-[#ff6fa3] to-[var(--color-lottery)] " +
  "border-2 border-[rgba(255,255,255,0.22)] " +
  "shadow-[0_0_0_5px_rgba(255,3,96,0.14),0_6px_20px_rgba(255,3,96,0.52),0_18px_50px_rgba(255,3,96,0.22)] " +
  "hover:shadow-[0_0_0_8px_rgba(255,3,96,0.18),0_8px_28px_rgba(255,3,96,0.68),0_22px_56px_rgba(255,3,96,0.3)] " +
  "hover:scale-[1.07] active:scale-95";

/* ── Expanded action tray ─────────────────────────────────── */
const walletMenuBaseCls =
  "flex flex-row items-center gap-3 bg-transparent rounded-[var(--radius-xl)] py-2 px-3 absolute bottom-0 -right-3";
const walletMenuOpenCls = "animate-wallet-expand";
const walletMenuCollapsingCls = "animate-wallet-collapse [&_.wallet-icon-opt]:animate-wallet-icon-out";

/* ── Shared action button base ────────────────────────────── */
const walletIconOptionCls =
  "wallet-icon-opt flex flex-col items-center justify-center gap-1.5 border-0 cursor-pointer " +
  "transition-all duration-300 opacity-0 scale-[0.72] translate-y-[10px] animate-wallet-icon-in " +
  "hover:scale-[1.06] active:scale-94 " +
  "[&:nth-child(1)]:[animation-delay:0.07s] [&:nth-child(2)]:[animation-delay:0.14s] [&:nth-child(3)]:[animation-delay:0.21s]";

/* ── Deposit: bright-pink → lottery ───────────────────────── */
const depositButtonCls =
  "!w-[62px] !h-[70px] !rounded-[var(--radius-xl)] pt-3 pb-2 px-0 " +
  "bg-gradient-to-b from-[#ff79ac] to-[var(--color-lottery)] " +
  "border border-t-[rgba(255,255,255,0.28)] border-[rgba(255,255,255,0.1)] " +
  "shadow-[0_5px_20px_rgba(255,3,96,0.52),0_2px_5px_rgba(0,0,0,0.28)]";

/* ── Withdraw: lottery → magenta ──────────────────────────── */
const withdrawButtonCls =
  "!w-[62px] !h-[70px] !rounded-[var(--radius-xl)] pt-3 pb-2 px-0 " +
  "bg-gradient-to-b from-[var(--color-lottery)] to-[var(--color-accent-magenta)] " +
  "border border-t-[rgba(255,255,255,0.22)] border-[rgba(255,255,255,0.1)] " +
  "shadow-[0_5px_20px_rgba(215,50,168,0.52),0_2px_5px_rgba(0,0,0,0.28)]";

/* ── Cancel: frosted glass, visually recessive ────────────── */
const cancelButtonCls =
  "!w-[42px] !h-[42px] !rounded-full p-0 flex items-center justify-center " +
  "bg-[rgba(18,18,32,0.72)] backdrop-blur-md " +
  "border border-[rgba(255,255,255,0.1)] " +
  "shadow-[0_2px_10px_rgba(0,0,0,0.4)] " +
  "hover:bg-[rgba(255,255,255,0.1)] transition-all duration-200";

/* ── Icon frosted bubble (inside action buttons) ──────────── */
const iconBubbleCls =
  "w-[32px] h-[32px] rounded-full bg-[rgba(255,255,255,0.18)] flex items-center justify-center pointer-events-none";

/* ── Action button label ──────────────────────────────────── */
const iconLabelCls =
  "text-[10px] font-bold text-white/90 text-center leading-none tracking-wide pointer-events-none";

const FloatButtonWallet = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Listen for floating menu events
  useEffect(() => {
    const handleFloatingMenuOpen = () => {
      if (isExpanded) {
        // Start closing animation
        setIsClosing(true);
        // Wait for animation to complete before hiding
        setTimeout(() => {
          setIsExpanded(false);
          setIsClosing(false);
        }, 800); // Match the collapse animation duration
      }
    };

    const handleMultiGameMenuOpen = () => {
      if (isExpanded) {
        // Start closing animation
        setIsClosing(true);
        // Wait for animation to complete before hiding
        setTimeout(() => {
          setIsExpanded(false);
          setIsClosing(false);
        }, 800); // Match the collapse animation duration
      }
    };

    window.addEventListener("floating-menu-opened", handleFloatingMenuOpen);
    window.addEventListener("multi-game-menu-opened", handleMultiGameMenuOpen);

    return () => {
      window.removeEventListener("floating-menu-opened", handleFloatingMenuOpen);
      window.removeEventListener("multi-game-menu-opened", handleMultiGameMenuOpen);
    };
  }, [isExpanded]);
  const authStatus = useAuthStore(state => state.authStatus);
  const handleBankAccountModal = useBalanceStore(state => state.handleBankAccountModal);
  const handleWithdrawalModal = useBalanceStore(state => state.handleWithdrawalModal);

  const {t} = useTranslation();

  const toggleWalletMenu = () => {
    if (isExpanded) {
      // Start closing animation
      setIsClosing(true);
      // Wait for animation to complete before hiding
      setTimeout(() => {
        setIsExpanded(false);
        setIsClosing(false);
      }, 800); // Match the collapse animation duration
    } else {
      // Opening wallet menu - dispatch event to close floating menu
      window.dispatchEvent(new CustomEvent("wallet-menu-opened"));
      setIsExpanded(true);
      setIsClosing(false);
    }
    trackDepositButtonClick();
  };

  const handleDeposit = () => {
    handleBankAccountModal();
    // Start closing animation
    setIsClosing(true);
    setTimeout(() => {
      setIsExpanded(false);
      setIsClosing(false);
    }, 800);
  };

  const handleWithdrawal = () => {
    handleWithdrawalModal();
    // Start closing animation
    setIsClosing(true);
    setTimeout(() => {
      setIsExpanded(false);
      setIsClosing(false);
    }, 800);
  };

  return (
    <>
      {authStatus === "authenticated" && (
        <div
          id="float-wallet-btn"
          className={walletContainerCls}
        >
          {(isExpanded || isClosing) && (
            <div className={`${walletMenuBaseCls} ${walletMenuOpenCls} ${isClosing ? walletMenuCollapsingCls : ""}`}>
              <button
                type="button"
                className={`${walletIconOptionCls} ${depositButtonCls}`}
                onClick={handleDeposit}
              >
                <div className={iconBubbleCls}>
                  <ArrowDown size={18} weight="bold" color="white" />
                </div>
                <span className={iconLabelCls}>{t("floatBtnDeposit")}</span>
              </button>
              <button
                type="button"
                className={`${walletIconOptionCls} ${withdrawButtonCls}`}
                onClick={handleWithdrawal}
              >
                <div className={iconBubbleCls}>
                  <ArrowUp size={18} weight="bold" color="white" />
                </div>
                <span className={iconLabelCls}>{t("floatBtnWithdrawl")}</span>
              </button>
              <button
                type="button"
                className={`${walletIconOptionCls} ${cancelButtonCls}`}
                onClick={toggleWalletMenu}
              >
                <X size={15} weight="bold" color="rgba(255,255,255,0.75)" />
              </button>
            </div>
          )}
          {!isExpanded && !isClosing && (
            <button
              type="button"
              className={walletButtonCls}
              onClick={toggleWalletMenu}
            >
              <Wallet size={28} weight="fill" color="white" />
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default FloatButtonWallet;
